import { useMemo, useSyncExternalStore } from "react";
import type { DisplayGame } from "../types/nba";

/**
 * Hide scores (DSPRT-330): for anyone who watches a game after it ends. With
 * the switch on, every finished game loses its score, its winner and its
 * top performer before a component sees it, so nothing downstream can
 * draw them: the score strip, the home slides, Scores and a team's schedule
 * all read games through the hooks that call useMasked. "Show" on one game
 * reveals that game until the page is left; the switch stays on. Opening a
 * game's box score is asking for the score, and shows it.
 *
 * A game in progress is left alone (DSPRT-340): whoever is on the site while
 * it is being played is following it. Its score goes blank when it ends.
 *
 * Only the last week's games are kept back (HIDE_DAYS): nobody is saving a
 * game from March to watch, and a team's schedule with every result of the
 * season blanked would be no schedule. And only where games are listed: a
 * page that is about results (a box score, two clubs' meetings, a title
 * run) asks for its games unmasked.
 *
 * The same switch keeps back a finished UFC card's results (DSPRT-347); that
 * half is in ufc/hide.ts.
 *
 * Kept on the device, off unless switched on. It hides what the site draws;
 * a final-score alert is a separate thing with its own switch.
 */
export const HIDE_DAYS = 7;
const KEY = "statsummit:hide-scores";

function read(): boolean {
  try {
    return localStorage.getItem(KEY) === "1";
  } catch {
    return false;
  }
}

let hide = read();
const shown = new Set<string>();
/**
 * What a shared link keeps back (DSPRT-418): a game's id, "ufc-fight:<bout
 * id>", or a card's "ufc:<slug>" once its fight is read. Opened from a link
 * with ?hide=1, the page hides scores for the visit (not saved), whatever
 * age the game is, and the one shared opens behind "Show score".
 */
const held = new Set<string>();
// A new value each time anything changes: what the hook hands React to compare.
let version = 0;
const listeners = new Set<() => void>();
const tell = () => {
  version++;
  listeners.forEach((l) => l());
};

/** Keep this back as a shared link asked (DSPRT-418): scores are hidden for this visit, and this one whatever its age. */
export function holdShared(id: string): void {
  if (held.has(id)) return;
  held.add(id);
  hide = true;
  tell();
}

/** Whether a shared link is keeping this back still: held and not yet shown. */
export function sharedHeld(id: string | null | undefined): boolean {
  return !!id && held.has(id) && !shown.has(id);
}

/** Whether this was held by a shared link at all, shown since or not. */
export function heldByLink(id: string): boolean {
  return held.has(id);
}

/** The address without its ?hide=1, once what it held is shown. */
export function dropHideParam(): void {
  try {
    const u = new URL(window.location.href);
    if (!u.searchParams.has("hide")) return;
    u.searchParams.delete("hide");
    window.history.replaceState(window.history.state, "", u.pathname + u.search + u.hash);
  } catch {
    // Nothing to tidy.
  }
}

// A shared game or fight (/game/401811041?hide=1, /ufc/fight/9434?hide=1),
// read once as the app starts, before anything draws a score.
if (typeof window !== "undefined") {
  try {
    const u = new URL(window.location.href);
    if (u.searchParams.get("hide") === "1") {
      const g = /^\/game\/(\d{6,12})\/?$/.exec(u.pathname);
      const f = /^\/ufc\/fight\/(\d{1,12})\/?$/.exec(u.pathname);
      if (g) holdShared(g[1]);
      if (f) holdShared(`ufc-fight:${f[1]}`);
    }
  } catch {
    // An address the URL parser won't take: nothing held.
  }
}

export function hidingScores(): boolean {
  return hide;
}

export function setHideScores(next: boolean): void {
  if (next === hide) return;
  hide = next;
  // Switching it back on hides everything again, the games shown one by one included.
  shown.clear();
  try {
    if (next) localStorage.setItem(KEY, "1"); else localStorage.removeItem(KEY);
  } catch {
    // Private mode: it holds for this visit.
  }
  tell();
}

/** Show one game's score from here on, wherever it is drawn. */
export function showScore(gameId: string): void {
  if (shown.has(gameId)) return;
  shown.add(gameId);
  tell();
}

/** Whether something shown one by one (a game by its id, a UFC card by "ufc:<slug>") has been asked for. */
export function revealed(id: string): boolean {
  return shown.has(id);
}

/** A number that changes whenever the switch or a shown item does: what a memo over masked data depends on. */
export function useSpoilerVersion(): number {
  return useSyncExternalStore(subscribe, () => version, () => 0);
}

/** Whether this game's score is being kept back, for a list that isn't made of games (Your players). */
export function scoreHidden(gameId: string | null | undefined): boolean {
  return hide && !!gameId && !shown.has(gameId);
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

/** Whether scores are hidden; re-renders when the switch or a shown game changes. */
export function useHideScores(): boolean {
  useSyncExternalStore(subscribe, () => version, () => 0);
  return hide;
}

/** A finished game without what gives it away. `hidden` tells the card to offer "Show". */
function mask(g: DisplayGame, since: string): DisplayGame {
  if (g.state !== "FINAL") return g;
  if (shown.has(g.id) || (g.date < since && !held.has(g.id))) return g;
  return {
    ...g,
    // The record goes with the score (NBA-02, DSPRT-593): in opening week
    // "1-0" beside a masked score is the result.
    home: { ...g.home, score: null, record: null },
    away: { ...g.away, score: null, record: null },
    top: null,
    seriesSummary: null,
    seriesWinsHome: null,
    seriesWinsAway: null,
    hidden: true,
  };
}

/**
 * The games as the reader may see them: as they are, or masked while scores
 * are hidden. `on` false is a page that is about the results themselves.
 */
export function useMasked(games: DisplayGame[], on = true): DisplayGame[] {
  const v = useSyncExternalStore(subscribe, () => version, () => 0);
  return useMemo(() => {
    if (!hide || !on) return games;
    const since = new Date(Date.now() - HIDE_DAYS * 86_400_000).toISOString().slice(0, 10);
    return games.map((g) => mask(g, since));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [games, v, on]);
}
